import { useMutation } from '@apollo/client';
import { IconCopy } from '@tabler/icons-react';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { ItemTypeChoices } from '../../../../__generated__/graphql';
import { COPY_QUOTATION_ITEM, MOVE_QUOTATION_ITEM_MUTATION } from '../../../../api/mutations/quotations/item';
import { QUOTATION_QUERY } from '../../../../api/queries/quotations/quotation';
import { useAppDispatch, useAppSelector } from '../../../../helpers/reduxHooks';
import { discardAlert, setErrorAlert, setSuccessAlert } from '../../../../redux/alertSlice';
import { setIsLoadingUpdate, unsetCopiedItem, unsetItemToCut } from '../../../../redux/quotationSlice';
import { FlattenedItem } from '../../../shared/dnd/types';

interface Props {
  item: FlattenedItem,
}

export default function PasteQuotationItem(props: Props) {
  const { item } = props;
  const { quotationId } = useParams();
  const copiedItem = useAppSelector(state => state.quotation.copiedItem);
  const itemToCut = useAppSelector(state => state.quotation.itemToCut);
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const [
    copyQuotationItem,
    {
      loading: copyQuotationItemLoading,
      data: copyQuotationItemData,
    },
  ] = useMutation(COPY_QUOTATION_ITEM, {
    refetchQueries: [{
      query: QUOTATION_QUERY,
      variables: {
        quotation: quotationId as string,
      },
    }],
  });

  const [
    moveQuotationItem,
    {
      loading: moveQuotationItemLoading,
      data: moveQuotationItemData,
    },
  ] = useMutation(MOVE_QUOTATION_ITEM_MUTATION, {
    refetchQueries: [{
      query: QUOTATION_QUERY,
      variables: {
        quotation: quotationId as string,
      },
    }],
  });

  const handlePasteTreeItem = () => {      
    if (itemToCut) {
      if (!item?.parentId 
        && (itemToCut.itemType === ItemTypeChoices.Product || itemToCut.itemType === ItemTypeChoices.Service)) {
        dispatch(setErrorAlert([t('Only Groups are allowed to be pasted at the root level')]));
        return;
      }
      dispatch(discardAlert());
      moveQuotationItem(
        {
          variables: {
            order: item?.order as number + 1,
            parent: item?.parentId as string,
            items: [itemToCut.id as string],
            quotation: quotationId as string,
          },
          onError: () => {
            dispatch(setIsLoadingUpdate(false));
          },
        },
      );
      return;
    }
    
    if (copiedItem) {
      dispatch(discardAlert());
      copyQuotationItem(
        {
          variables: {
            items: [copiedItem?.id as string],
            order: item.order as number + 1,
            parent: item.parentId as string,
            quotation: quotationId as string,
          },
          onError: () => {
            dispatch(setIsLoadingUpdate(false));
          },
        },
      );
    }
  };

  useEffect(() => {
    dispatch(setIsLoadingUpdate(copyQuotationItemLoading || moveQuotationItemLoading));
  }, [dispatch, copyQuotationItemLoading, moveQuotationItemLoading]);

  useEffect(() => {
    if (
      (copyQuotationItemData && copyQuotationItemData.copyQuotationItems && copyQuotationItemData.copyQuotationItems.response) 
      || (moveQuotationItemData && moveQuotationItemData.moveQuotationItems && moveQuotationItemData.moveQuotationItems.response)) {
      dispatch(setSuccessAlert(
        [`${t('Item was successfully pasted')}`],
      ));
      dispatch(unsetCopiedItem());
      dispatch(unsetItemToCut());
    }
  }, [copyQuotationItemData, dispatch, moveQuotationItemData, t]);

  return (
    <div
      onClick={handlePasteTreeItem}
    >
      <IconCopy className="h-6 w-6 cursor-pointer hover:bg-cblue-200" />
    </div>
  );
}
